import { useMemo } from 'react';
import { ProgressBar, Spinner } from '@heroui/react';
import { Widget } from '@heroui-pro/react';
import { useAppSelector } from '@hs/shared/stores/hooks';
import { trpc } from '@/lib/trpc';

const STORAGE_RESOURCE_CODE = 'STORAGE';
const AI_CREDITS_RESOURCE_CODE = 'AI_CREDITS';
const BYTES_PER_G = 1024 * 1024 * 1024;
const CREDITS_PER_THOUSAND = 1000;

const RESOURCE_CARDS = [
  { code: STORAGE_RESOURCE_CODE, name: '存储空间', unit: 'G' },
  { code: AI_CREDITS_RESOURCE_CODE, name: 'AI积分', unit: '千点' },
] as const;

type EntitlementRow = {
  resourceCode?: string | null;
  quota?: number | string | null;
  used?: number | string | null;
  remaining?: number | string | null;
};

function toNumber(value: number | string | null | undefined): number {
  if (value == null) return 0;
  const parsed = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(parsed) ? parsed : 0;
}

function convertAmount(resourceCode: string, value: number): number {
  if (resourceCode === STORAGE_RESOURCE_CODE) return value / BYTES_PER_G;
  if (resourceCode === AI_CREDITS_RESOURCE_CODE) return value / CREDITS_PER_THOUSAND;
  return value;
}

function formatAmount(value: number): string {
  if (!Number.isFinite(value)) return '0';
  const rounded = Math.round(value * 100) / 100;
  if (rounded === 0) return '0';
  return rounded.toLocaleString('zh-CN', { maximumFractionDigits: 2 });
}

function usagePercent(quota: number, used: number): number {
  if (quota <= 0) return used > 0 ? 100 : 0;
  return Math.min(100, Math.max(0, (used / quota) * 100));
}


export function SpaceEntitlementPanel() {
  const user = useAppSelector((state) => state.auth.user);
  const isHydrated = useAppSelector((state) => state.auth.isHydrated);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const spaceId = user?.spaces?.[0]?.id;

  const {
    data: entitlements = [],
    isLoading: entitlementsLoading,
    isError: entitlementsError,
    error: entitlementsErrorDetail,
  } = trpc.spaceEntitlements.listAll.useQuery(
    { spaceId: spaceId! },
    { enabled: isHydrated && isAuthenticated && Boolean(spaceId) }
  );

  const entitlementByCode = useMemo(() => {
    const map = new Map<string, EntitlementRow>();
    for (const row of entitlements as EntitlementRow[]) {
      const code = row.resourceCode?.trim();
      if (code) map.set(code, row);
    }
    return map;
  }, [entitlements]);

  const cards = useMemo(() => {
    return RESOURCE_CARDS.map((product) => {
      const row = entitlementByCode.get(product.code);
      const quota = convertAmount(product.code, toNumber(row?.quota));
      const used = convertAmount(product.code, toNumber(row?.used));
      const remaining = convertAmount(product.code, toNumber(row?.remaining));
      return {
        product,
        resourceCode: product.code,
        quota,
        used,
        remaining,
        unit: product.unit,
        percent: usagePercent(quota, used),
      };
    });
  }, [entitlementByCode]);

  const isLoading = entitlementsLoading;
  const isError = entitlementsError;
  const errorMessage = entitlementsErrorDetail?.message || '未知错误';

  if (!spaceId) {
    return (
      <div className="flex h-full min-h-0 flex-col overflow-auto p-6">
        <p className="text-sm text-default-500">暂无空间信息，无法加载权益。</p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-auto p-6">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-foreground">权益管理</h2>
        <p className="mt-2 text-sm text-default-500">
          为避免影响使用，请及时对即将用尽的权益进行扩容
        </p>
      </div>

      {isLoading ? (
        <div className="flex min-h-[40vh] items-center justify-center">
          <Spinner className="size-8" />
        </div>
      ) : isError ? (
        <div className="flex min-h-[40vh] flex-col items-center justify-center gap-2">
          <p className="text-danger">加载失败</p>
          <p className="text-sm text-default-500">{errorMessage}</p>
        </div>
      ) : cards.length === 0 ? (
        <div className="flex min-h-[12rem] items-center justify-center">
          <p className="text-sm text-default-500">暂无可扩容的资源产品</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {cards.map(({ product, quota, used, remaining, unit, percent }) => (
            <Widget key={product.code}>
              <Widget.Header>
                <Widget.Title>{product.name || '未命名产品'}</Widget.Title>
                <Widget.Description>{unit ? `单位：${unit}` : '单位：—'}</Widget.Description>
              </Widget.Header>
              <Widget.Content>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <p className="text-sm text-default-500">配额</p>
                    <p className="mt-1 text-3xl font-bold tabular-nums text-foreground">
                      {formatAmount(quota)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-default-500">已用</p>
                    <p className="mt-1 text-3xl font-bold tabular-nums text-foreground">
                      {formatAmount(used)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-default-500">剩余</p>
                    <p className="mt-1 text-3xl font-bold tabular-nums text-foreground">
                      {formatAmount(remaining)}
                    </p>
                  </div>
                </div>

                <ProgressBar
                  aria-label={`${product.name || '权益'} 使用进度`}
                  className="mt-5 w-full"
                  maxValue={100}
                  minValue={0}
                  value={percent}
                >
                  <ProgressBar.Track className="h-2 rounded-full bg-default-200">
                    <ProgressBar.Fill className="h-full rounded-full bg-danger" />
                  </ProgressBar.Track>
                </ProgressBar>

              </Widget.Content>
            </Widget>
          ))}
        </div>
      )}

    </div>
  );
}
